<template>
  <div
    v-if="isMobileFiltersOpen"
    class="tw:fixed tw:inset-0 tw:top-0 tw:z-51 tw:w-full tw:h-full tw:overflow-hidden tw:flex tw:overflow-y-auto tw:z-100"
  >
    <div
      class="tw:mx-auto tw:w-full tw:max-w-[980px] tw:bg-white tw:m-auto box-shadow tw:relative"
      v-click-outside="closeMobileFilters"
    >
      <div
        class="tw:fixed tw:z-50 tw:w-full tw:h-[72px] tw:bg-white tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-3.5 tw:px-5 tw:text-gray-800"
      >
        <div>
          <button
            class="tw:outline-hidden tw:pr-2 tw:pt-2"
            @click="closeMobileFilters"
            @keydown.enter="closeMobileFilters"
          >
            <Close class="tw:size-2.5" />
          </button>
        </div>
        <div class="tw:font-bold">{{ $t('filters') }}</div>
        <div>
          <button
            class="tw:text-blue-700 tw:font-bold tw:disabled:text-gray-400 tw:hover:cursor-pointer"
            :disabled="propertyTempFiltersArray.length === 0 && statusTempFiltersArray.length === 0"
            @click="resetFilters"
          >
            {{ $t('rc_filter_reset') }}
          </button>
        </div>
      </div>

      <div class="tw:flex tw:flex-col tw:mb-[76px] tw:mt-[72px] tw:min-h-screen tw:p-5">
        <MobileSelectPropertyFilter
          type="mobile"
          :label="$t('hotels')"
          :options="propertiesOptions"
          name="properties"
          :value="propertyTempFiltersArray"
          @onSelect="onSelectTempPropertyFilters"
        />
        <div class="tw:border-t tw:border-gray-200 tw:h-[1px] tw:w-full tw:my-3" />
        <MobileSelectStatusFilter
          :label="$t('tbs_invoice_status')"
          :options="statusOptions"
          name="status"
          :value="statusTempFiltersArray"
          @onSelect="onSelectTempStatusFilters"
        />
      </div>

      <div
        class="footer shadow tw:fixed tw:bottom-0 tw:w-full tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-4 tw:px-8 tw:text-gray-800 tw:bg-white"
      >
        <div class="tw:md:flex tw:lg:hidden tw:w-full">
          <MainButton class="tw:w-full" @click="applyFilters">{{ $t('apply') }}</MainButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref } from 'vue'

import MainButton from '@/components/base/buttons/MainButton.vue'
import Close from '@/components/icons/Close.svg?component'
import MobileSelectPropertyFilter from '@/components/billing/premiumProducts/filters/MobileSelectPropertyFilter.vue'
import MobileSelectStatusFilter from '@/components/billing/premiumProducts/filters/MobileSelectStatusFilter.vue'

import { usePremiumProductsFilters } from '@/components/billing/premiumProducts/usePremiumProductsFilters'

const { isMobileFiltersOpen, closeMobileFilters } = usePremiumProductsFilters()
const emit = defineEmits(['applyFilters', 'resetFilters'])
const applyFilters = () =>
  emit('applyFilters', {
    property: propertyTempFiltersArray.value,
    status: statusTempFiltersArray.value,
  })

const props = defineProps({
  mobileFiltersOpen: Boolean,
  propertiesOptions: Array,
  propertiesValue: Array,
  statusOptions: Array,
  statusValue: Array,
})

const resetTempFilters = () => {
  propertyTempFiltersArray.value = [...props.propertiesValue]
  statusTempFiltersArray.value = [...props.statusValue]
}

const resetFilters = () => {
  resetTempFilters()
  emit('resetFilters')
}

const propertyTempFiltersArray = ref([...props.propertiesValue])
const statusTempFiltersArray = ref([...props.statusValue])

const onSelectTempPropertyFilters = newValue => {
  const isAllOption = newValue.id === 0
  const currentHasAll = propertyTempFiltersArray.value.some(item => item.id === 0)

  if (isAllOption) {
    propertyTempFiltersArray.value = [newValue]
  } else {
    if (currentHasAll) {
      propertyTempFiltersArray.value = propertyTempFiltersArray.value.filter(item => item.id !== 0)
    }

    const index = propertyTempFiltersArray.value.findIndex(item => item.id === newValue.id)
    if (index > -1) {
      propertyTempFiltersArray.value.splice(index, 1)

      if (propertyTempFiltersArray.value.length === 0) {
        propertyTempFiltersArray.value = [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }]
      }
    } else {
      propertyTempFiltersArray.value.push(newValue)
    }
  }
}

const isStatusSelected = itemId => {
  return statusTempFiltersArray.value.some(item => item.id === itemId)
}

const onSelectTempStatusFilters = selection => {
  if (selection.group) {
    const { options } = selection
    const isAnyGroupItemSelected = options.some(opt => isStatusSelected(opt.id))

    if (isAnyGroupItemSelected) {
      const groupItemIds = options.map(opt => opt.id)
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => !groupItemIds.includes(item.id))
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => item.id !== 'all')
    } else {
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => item.id !== 'all')
      options.forEach(option => {
        statusTempFiltersArray.value.push(option)
      })
    }
    return
  }

  const newValue = selection
  const isAllOption = newValue.id === 'all'
  if (isAllOption) {
    const allIsCurrentlySelected = statusTempFiltersArray.value.some(item => item.id === 'all')

    if (allIsCurrentlySelected) {
      statusTempFiltersArray.value = []
    } else {
      statusTempFiltersArray.value = []
      statusTempFiltersArray.value.push(newValue)
    }
  } else {
    const currentlySelected = isStatusSelected(newValue.id)

    if (currentlySelected) {
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => item.id !== newValue.id)
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => item.id !== 'all')
    } else {
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => item.id !== 'all')
      statusTempFiltersArray.value.push(newValue)
    }
  }
}
</script>
